import { useCallback, useEffect, useRef, useState } from 'react';
import { cloudFunctionV2 } from '../functions';

// Shared cache across all instances of the hook — re-opening modals or
// switching pages keeps prior suggestions warm. TTL guards against stale REAPI
// data; LRU cap keeps memory bounded.
const CACHE_MAX = 50;
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

const normalizeKey = (s) => s.trim().toLowerCase();

const cacheGet = (key) => {
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL_MS) {
        cache.delete(key);
        return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry.results;
};

const cacheSet = (key, results) => {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, { results, ts: Date.now() });
    while (cache.size > CACHE_MAX) {
        const oldest = cache.keys().next().value;
        cache.delete(oldest);
    }
};

const mapResults = (responses) => responses.map((item) => ({
    label: item.address,
    id: item.id,
    countyId: item.fips,
    latitude: item.latitude,
    longitude: item.longitude,
    zipcode: item.zip
}));

export function useAddressAutocomplete(options = {}) {
    const { minChars = 3, debounceMs = 250 } = options;

    const [query, setQueryState] = useState('');
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const debounceRef = useRef(null);
    const abortRef = useRef(null);
    // Tracks the latest query the user has typed. Every async resolver checks
    // this before committing so that a slow response for an earlier prefix can
    // never overwrite results for a newer one.
    const currentQueryRef = useRef('');

    const cancelInFlight = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);

    const runSearch = useCallback(async (searchVal) => {
        const key = normalizeKey(searchVal);

        const cached = cacheGet(key);
        if (cached) {
            if (currentQueryRef.current === searchVal) {
                setResults(cached);
                setIsSearching(false);
            }
            return;
        }

        const controller = new AbortController();
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = controller;
        setIsSearching(true);

        const url = process.env.REACT_APP_REAPI_AUTOCOMPLETE_URL;
        const response = await cloudFunctionV2(url, { address: searchVal }, { signal: controller.signal });

        if (currentQueryRef.current !== searchVal) return;
        if (controller.signal.aborted) return;

        if (response.status === 200 && Array.isArray(response.data)) {
            const mapped = response.data.length > 0 ? mapResults(response.data) : [];
            cacheSet(key, mapped);
            setResults(mapped);
        } else {
            setResults([]);
        }
        setIsSearching(false);
        if (abortRef.current === controller) abortRef.current = null;
    }, []);

    const setQuery = useCallback((value, { immediate = false } = {}) => {
        const safeValue = value ?? '';
        setQueryState(safeValue);
        currentQueryRef.current = safeValue;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }

        const trimmed = safeValue.trim();
        if (trimmed.length === 0 || trimmed.length < minChars) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        const cached = cacheGet(normalizeKey(safeValue));
        if (cached) {
            setResults(cached);
            setIsSearching(false);
            return;
        }

        if (immediate) {
            runSearch(safeValue);
        } else {
            debounceRef.current = setTimeout(() => runSearch(safeValue), debounceMs);
        }
    }, [minChars, debounceMs, runSearch]);

    // Accepts an optional final display value (e.g. the picked address label)
    // so callers can both close the dropdown and set the input to the selected
    // label in one call, without re-triggering a search.
    const reset = useCallback((finalValue = '') => {
        cancelInFlight();
        setQueryState(finalValue);
        currentQueryRef.current = finalValue;
        setResults([]);
        setIsSearching(false);
    }, [cancelInFlight]);

    useEffect(() => {
        return () => cancelInFlight();
    }, [cancelInFlight]);

    return { query, setQuery, results, isSearching, reset };
}

export default useAddressAutocomplete;
